import Button from "app/storybookComponents/Button";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectDeactivatedUsers,
  selectDepartments,
  selectIsDepartmentsHidden,
} from "app/containers/AdminConsole/slice";
import SortableTable from "app/components/SortableTable";
import {
  selectAllCompanyUsersById,
  selectSampleUsersInfoById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import CheckInResponseSummaryModal from "../Modals/CheckInResponseSummaryModal";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getOutOfFiveColor } from "app/containers/Dashboard/helpers";
import { useNavigate } from "react-router-dom";
import {
  GroupWeeklyCheckInTableRowData,
  isWeeklyCheckInRowUser,
  SelectedCheckInBreakdownEntity,
  WeeklyCheckInTableRowData,
  AllWeeklyCheckInTableData,
  UserWeeklyCheckInTableRowData,
} from "../types";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";
import {
  getDateForSorting,
  getWeeklyCheckInTableRowEntityId,
  getSearchInput,
  getDropdownFromRecord,
  getDateCell,
  getWeeklyCheckInBreakDownKey,
  getRiskPattern,
} from "../helpers";
import EmptyCard from "app/storybookComponents/Cards/EmptyCard";
import {
  WeeklyCheckInInitialTimeInterval,
  WeeklyCheckInTimeIntervals,
  WeeklyCheckInEntityTypes,
} from "app/containers/AdminConsole/constants";
import { trackCSVExport } from "utils/trackingFunctions";
import CheckinScorePopover from "app/components/Popovers/CheckinScorePopover";
import SimplePopover from "app/components/Popovers/SimplePopover";
import TableDropdownMenu from "app/components/Dropdowns/TableDropdownMenu";
import { responseStatus } from "utils/types";
import {
  getUserCheckInsFromEntityAndTimeInterval,
  regradeWeeklyCheckin,
  selectGetWeeklyCheckInBreakdownTableDataStatus,
  selectRegradeWeeklyCheckInStatusByThread,
  selectSendWeeklyCheckInRemindersStatus,
  selectWeeklyCheckInBreakdownTableData,
  selectWeeklyCheckInCompanySettings,
  selectWeeklyCheckInUserStatus,
  sendWeeklyCheckInReminders,
} from "../slice";
import { getPeopleOrPerson } from "utils/helperFunctions";
import ReminderButton from "../ReminderButton";
import IncompleteCheckInsModal from "../Modals/IncompleteCheckInsModal";
import SendAllPendingCheckinReminderWarningModal from "../Modals/SendAllPendingCheckinReminderWarningModal";
import HrConcernAlertsModal, {
  ConcernAlertRow,
} from "../Modals/HrConcernAlertsModal";
import {
  HR_CONCERN_SEVERITY_MAP,
  WEEKLY_CHECK_IN_PATTERN_CONTEXT,
  WEEKLY_CHECK_IN_TRAIT_NAMES,
  WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS,
} from "../constants";
import SupportOrCelebrateDot from "./SupportOrCelebrateDot";
import { TableHeader } from "app/components/SortableTable/types";
import { selectManagerAndDirectReportsByUserAccountId } from "app/containers/AdvancedCsvUpload/slice";
import { faArrowRotateForward } from "@fortawesome/pro-regular-svg-icons";
import { faCircle } from "@fortawesome/free-solid-svg-icons";

interface Props {
  allWeeklyCheckInTableData: AllWeeklyCheckInTableData;
  getWeeklyCheckInTableDataStatus: responseStatus;
  fetchTableData?: (params: {
    entityType: string;
    timeInterval: string;
  }) => void;
  hideDepartmentDropdown?: boolean;
  hideEntityDropdown?: boolean;
  isIndividualContributor?: boolean;
  breakdownRowSelected: SelectedCheckInBreakdownEntity | null; // Maybe think of adding to redux state to prevent prop drilling
  setBreakdownRowSelected: (
    breakdownRowSelected: SelectedCheckInBreakdownEntity
  ) => void;
  setEntitySelected: (entityType: string) => void;
  entitySelected: string;
  incompleteUserAccountIds?: number[];
  isSample?: boolean;
}

export interface CheckInActivityTableContentRef {
  exportCSV: () => { [key: string]: any }[];
}

const CheckInActivityTableContent = forwardRef<
  CheckInActivityTableContentRef,
  Props
>(
  (
    {
      getWeeklyCheckInTableDataStatus,
      allWeeklyCheckInTableData,
      fetchTableData,
      hideDepartmentDropdown = false,
      hideEntityDropdown,
      isIndividualContributor,
      breakdownRowSelected,
      setBreakdownRowSelected,
      setEntitySelected,
      incompleteUserAccountIds,
      entitySelected,
      isSample,
    }: Readonly<Props>,
    ref
  ) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const allTeamsById = useAppSelector(selectTeamsByTeamId);
    const departments = useAppSelector(selectDepartments);
    const usersById = useAppSelector(selectAllCompanyUsersById);
    const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
    const isDepartmentsHidden = useAppSelector(selectIsDepartmentsHidden);
    const weeklyCheckInUserStatus = useAppSelector(
      selectWeeklyCheckInUserStatus
    );
    const deactivatedUsers = useAppSelector(selectDeactivatedUsers);
    const sendEmailReminderStatus = useAppSelector(
      selectSendWeeklyCheckInRemindersStatus
    );
    const managersAndDirectReportsByUserAccountId = useAppSelector(
      selectManagerAndDirectReportsByUserAccountId
    );
    const weeklyCheckInCompanySettings = useAppSelector(
      selectWeeklyCheckInCompanySettings
    );
    const weeklyCheckInBreakdownTableData = useAppSelector(
      selectWeeklyCheckInBreakdownTableData
    );
    const getWeeklyCheckInBreakdownTableDataStatus = useAppSelector(
      selectGetWeeklyCheckInBreakdownTableDataStatus
    );
    const regradingWeeklyCheckinStatusByThread = useAppSelector(
      selectRegradeWeeklyCheckInStatusByThread
    );

    const [checkInResponseModalState, setCheckInResponseModalState] = useState<{
      isOpen: boolean;
      threadId: string;
      userAccountId: number | null;
      isSample: boolean;
      date?: string;
    }>({ isOpen: false, threadId: "", userAccountId: null, isSample: false });
    const [displayedInput, setDisplayedInput] = useState<string>("");
    const [timeInterval, setTimeInterval] = useState(
      WeeklyCheckInInitialTimeInterval
    );
    const [incompleteCheckInsModalState, setIncompleteCheckInsModalState] =
      useState<boolean>(false);
    const [isSendAllRemindersModalOpened, setIsSendAllRemindersModalOpened] =
      useState(false);
    const [isConcernAlertsModalOpen, setIsConcernAlertsModalOpen] =
      useState(false);

    const openCheckInResponseModal = (
      threadId: string,
      userAccountId: number,
      date?: string
    ) => {
      setCheckInResponseModalState({
        isOpen: true,
        threadId,
        userAccountId,
        isSample: !!isSample,
        date,
      });
    };

    const users = useMemo(() => {
      if (isSample) {
        return sampleUsersById;
      }
      return usersById;
    }, [isSample, sampleUsersById, usersById]);

    const fetchTableDataLoading = useMemo(() => {
      if (breakdownRowSelected) {
        return getWeeklyCheckInBreakdownTableDataStatus === "loading";
      }
      return getWeeklyCheckInTableDataStatus === "loading";
    }, [
      getWeeklyCheckInTableDataStatus,
      getWeeklyCheckInBreakdownTableDataStatus,
      breakdownRowSelected,
    ]);

    useEffect(() => {
      if (fetchTableData) {
        fetchTableData({
          entityType: entitySelected,
          timeInterval,
        });
      }
    }, [fetchTableData, timeInterval, entitySelected]);

    const formatNumber = (num: number): string => {
      // Check if the number is a whole number
      if (num % 1 === 0) {
        return num.toString();
      } else {
        // If the number has a decimal part, truncate to one decimal place
        return num.toFixed(1);
      }
    };

    const getNoScorePopoverElement = (
      trait?: string,
      threadId?: string,
      errorGeneratingScores?: boolean | 0 | 1,
      gradingCount: number = 0
    ) => {
      if (!trait || !threadId) return null;
      const icon = errorGeneratingScores
        ? "exclamation-triangle"
        : "circle-info";
      const message = errorGeneratingScores
        ? `An error occurred while trying to capture a ${trait} score for this Check-In response.`
        : `This Check-In response was not long enough to capture a ${trait} score.`;

      const maxTriesMetElement =
        gradingCount > 3 ? (
          <div
            style={{
              color: "#550023",
              backgroundColor: "#F7CCDD",
              border: "1px solid #F2B3CD",
              padding: "12px 16px",
              borderRadius: "4px",
            }}
          >
            <div className="row-gap-16px align-items-center">
              <FontAwesomeIcon
                icon={["fas", "exclamation-triangle"]}
                style={{ fontSize: "16px" }}
              />
              <p>This score can only be updated up to 3 times.</p>
            </div>
          </div>
        ) : null;
      const regradingWeeklyCheckinStatus =
        regradingWeeklyCheckinStatusByThread?.[threadId];

      return (
        <SimplePopover
          popoverClassName="checkin-score-popover"
          popoverContent={
            <div className="column-gap-16px">
              <p>{message}</p>
              {errorGeneratingScores ? (
                <div className="column-gap-8px">
                  <div>
                    <Button
                      variant="secondary-blue"
                      onClick={() => {
                        dispatch(regradeWeeklyCheckin(threadId));
                      }}
                      disabled={
                        regradingWeeklyCheckinStatus === "loading" ||
                        !!maxTriesMetElement
                      }
                    >
                      Try again
                    </Button>
                  </div>
                  {maxTriesMetElement}
                </div>
              ) : null}
            </div>
          }
          trigger="hover"
          hideTitleAndCloseButton
        >
          <FontAwesomeIcon
            icon={["far", icon]}
            style={{ paddingLeft: "5px" }}
          />
        </SimplePopover>
      );
    };

    const getScoreCell = (
      data: WeeklyCheckInTableRowData,
      traitScoreName: "moodScore" | "readinessScore" | "progressScore"
    ) => {
      const score = data[traitScoreName];
      const dotColor = getOutOfFiveColor(score ?? 0);
      const traitName = WEEKLY_CHECK_IN_TRAIT_NAMES[traitScoreName];
      const popoverElement =
        isWeeklyCheckInRowUser(data) && !score
          ? getNoScorePopoverElement(
              traitName,
              data.threadId,
              data.errorGeneratingScores,
              data.gradingCount
            )
          : null;

      const displayValue = score ? (
        <div className="row-gap-8px align-items-center">
          {formatNumber(Number(score))}
          <div className={`status-dot ${dotColor}`} />
        </div>
      ) : (
        <div className="d-flex">
          <p>-</p>
          {popoverElement}
        </div>
      );

      return {
        sortValue: score,
        displayValue,
      };
    };

    const getEntityDisplayValue = (
      title: {
        label: string;
        onClick?: (() => void) | null;
      },
      description?: string
    ) => {
      const entityLabel = <p className="user-name-cell__name">{title.label}</p>;
      return (
        <div>
          {title.onClick ? (
            <button className="button-link" onClick={title.onClick}>
              {entityLabel}
            </button>
          ) : (
            entityLabel
          )}
          {description ? (
            <p className="small-grey-text">{description}</p>
          ) : null}
        </div>
      );
    };

    const getUserCell = (
      userAccountId: number,
      threadId: string,
      date?: string
    ) => {
      const deactivatedUser = deactivatedUsers[userAccountId];
      const user = users[userAccountId] ?? deactivatedUser;
      if (!user) {
        return "";
      }
      const { firstName = "", lastName = "", jobTitle = "No job title" } = user;
      const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
      return {
        sortValue: fullName,
        displayValue: getEntityDisplayValue(
          {
            label: fullName,
            onClick: deactivatedUser
              ? null
              : () => {
                  setCheckInResponseModalState({
                    isOpen: true,
                    threadId,
                    userAccountId,
                    isSample: !!isSample,
                    date,
                  });
                },
          },
          deactivatedUser ? "(Deactivated)" : jobTitle
        ),
      };
    };

    const getDepartmentCell = (cellData: GroupWeeklyCheckInTableRowData) => {
      const { entityId: departmentId, invitedCount } = cellData;
      const department = departments[departmentId] ?? {};
      const { name = "" } = department;
      const memberString = `${invitedCount} invited`;

      return {
        sortValue: name,
        displayValue: getEntityDisplayValue(
          {
            label: name,
            onClick: () => onSetBreakdownState(cellData, "department"),
          },
          memberString
        ),
      };
    };

    const getTeamCell = (cellData: GroupWeeklyCheckInTableRowData) => {
      const { entityId: teamId, invitedCount } = cellData;
      const team = allTeamsById[teamId];
      const name = team?.teamName ?? "";
      const memberString = `${invitedCount} invited`;
      return {
        sortValue: name,
        displayValue: getEntityDisplayValue(
          {
            label: name,
            onClick: () => onSetBreakdownState(cellData, "team"),
          },
          memberString
        ),
      };
    };

    const percentageCell = (
      invitedCount: number = 0,
      completedCount: number = 0,
      stringAltOrder?: boolean,
      inverseDotColor?: boolean
    ) => {
      const completionRate = invitedCount
        ? (completedCount / invitedCount) * 100
        : 0;

      const completionFraction = `${completedCount}/${invitedCount}`;
      const completionRateString = `${completionRate.toFixed()} %`;
      const completionString = stringAltOrder
        ? `${completionRateString} (${completionFraction})`
        : `(${completionFraction}) ${completionRateString}`;

      const dotColor = getOutOfFiveColor(completionRate / 20, inverseDotColor);
      return {
        sortValue: completionRate,
        displayValue: (
          <div className="row-gap-8px align-items-center">
            <p style={{ minWidth: "79px" }}>{completionString}</p>
            <div className={`status-dot ${dotColor}`} />
          </div>
        ),
      };
    };

    const getUserDropdownCell = (data: UserWeeklyCheckInTableRowData) => {
      const { threadId, userAccountId, isSummaryHidden } = data;
      const onOpenCheckInResponseModal = () => {
        openCheckInResponseModal(threadId, userAccountId, data.completedDate);
      };

      if (isIndividualContributor && !isSummaryHidden) {
        return {
          displayValue: (
            <div>
              <Button
                variant="secondary-blue"
                onClick={onOpenCheckInResponseModal}
                className="float-right"
              >
                View response
              </Button>
            </div>
          ),
          sortValue: 0,
        };
      }

      const userProfileDropdownOption = deactivatedUsers[
        userAccountId
      ] ? null : (
        <>
          <Dropdown.Divider />
          <Dropdown.Item eventKey="profile">View profile</Dropdown.Item>
        </>
      );

      if (isSummaryHidden) {
        return {
          displayValue: (
            <Dropdown
              onSelect={(e) => {
                if (e === "profile") {
                  navigate(`/UserGuide/${userAccountId}`);
                }
              }}
              className="float-right"
            >
              <Dropdown.Toggle
                variant="outline-primary"
                id="dropdown-basic"
                className="no-caret"
              >
                <FontAwesomeIcon icon="ellipsis" />
              </Dropdown.Toggle>
              <TableDropdownMenu>
                <Dropdown.Item eventKey="summary" disabled>
                  <FontAwesomeIcon
                    icon={["far", "lock-keyhole"]}
                    style={{ color: "#805100", marginRight: "5px" }}
                  />
                  View response summary
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item eventKey="info" disabled>
                  <div
                    style={{
                      color: "#805100",
                      fontSize: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <p>
                      {weeklyCheckInCompanySettings?.visibilityDefault ===
                      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY
                        ? "This report is currently visible to this user, their manager and admins."
                        : "This report is currently visible to this user, their manager, their team leaders, and admins."}
                    </p>
                    <p>Please reach out to an admin to learn more.</p>
                  </div>
                </Dropdown.Item>
                {userProfileDropdownOption}
              </TableDropdownMenu>
            </Dropdown>
          ),
          sortValue: 0,
        };
      }

      return {
        displayValue: (
          <Dropdown
            onSelect={(e) => {
              if (e === "summary") {
                onOpenCheckInResponseModal();
              } else if (e === "profile") {
                navigate(`/UserGuide/${userAccountId}`);
              }
            }}
            className="float-right"
          >
            <Dropdown.Toggle
              variant="outline-primary"
              id="dropdown-basic"
              className="no-caret"
            >
              <FontAwesomeIcon icon="ellipsis" />
            </Dropdown.Toggle>
            <TableDropdownMenu>
              <Dropdown.Item eventKey="summary">
                View response summary
              </Dropdown.Item>
              {userProfileDropdownOption}
            </TableDropdownMenu>
          </Dropdown>
        ),
        sortValue: 0,
      };
    };

    const onSetBreakdownState = (
      data: GroupWeeklyCheckInTableRowData,
      entityType: string
    ) => {
      if (entityType !== "team" && entityType !== "department") {
        return;
      }
      const { entityId, weeklyCheckInInstanceId } = data;
      setEntitySelected("user");
      setDisplayedInput("");

      dispatch(
        getUserCheckInsFromEntityAndTimeInterval({
          entityId,
          entityType,
          weeklyCheckInInstanceId,
        })
      );
      setBreakdownRowSelected({
        entityType,
        entityId,
        weeklyCheckInInstanceId,
        startDate: data.startDate,
        endDate: data.endDate,
      });
    };

    const getGroupDropdownCell = (data: GroupWeeklyCheckInTableRowData) => {
      const { entityId } = data;
      const onDropdownSelect = (e: string | null) => {
        if (e === "breakdown") {
          return onSetBreakdownState(data, entitySelected);
        }

        const basePath =
          entitySelected === "department" ? "/DepartmentGuide" : "/TeamGuide";
        navigate(`${basePath}/${entityId}`);
      };

      return {
        displayValue: (
          <Dropdown onSelect={onDropdownSelect} style={{ marginLeft: "auto" }}>
            <Dropdown.Toggle
              variant="outline-primary"
              id="dropdown-basic"
              className="no-caret"
            >
              <FontAwesomeIcon icon="ellipsis" />
            </Dropdown.Toggle>
            <TableDropdownMenu>
              <Dropdown.Item eventKey="breakdown">
                View activity breakdown
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item eventKey="profile">View profile</Dropdown.Item>
            </TableDropdownMenu>
          </Dropdown>
        ),
        sortValue: 0,
      };
    };

    const weeklyCheckInTableDataShowing = useMemo(() => {
      if (breakdownRowSelected) {
        return (
          weeklyCheckInBreakdownTableData[
            getWeeklyCheckInBreakDownKey(breakdownRowSelected)
          ] ?? null
        );
      }

      // Sample data's range keys (pastYear, pastMonth...) don't match the dropdown's, and the
      // dropdown is hidden in sample mode, so always show the all-time sample rows.
      const dataTimeInterval = isSample ? "allTime" : timeInterval;
      return Object.values(
        allWeeklyCheckInTableData?.[dataTimeInterval]?.[entitySelected] ?? {}
      );
    }, [
      breakdownRowSelected,
      allWeeklyCheckInTableData,
      entitySelected,
      isSample,
      timeInterval,
      weeklyCheckInBreakdownTableData,
    ]);

    const getFilteredRows = useCallback((): WeeklyCheckInTableRowData[] => {
      // If there is no input, return all rows
      if (!displayedInput) {
        return weeklyCheckInTableDataShowing ?? [];
      }

      if (entitySelected === "team") {
        return (
          weeklyCheckInTableDataShowing?.filter((row) => {
            const team = allTeamsById[getWeeklyCheckInTableRowEntityId(row)];
            if (!team) {
              return false;
            }
            return team.teamName
              .toLowerCase()
              .includes(displayedInput.toLowerCase());
          }) ?? []
        );
      }

      if (entitySelected === "department") {
        return (
          weeklyCheckInTableDataShowing?.filter((row) => {
            const department =
              departments[getWeeklyCheckInTableRowEntityId(row)];
            if (!department) {
              return false;
            }
            return department.name
              ?.toLowerCase()
              .includes(displayedInput.toLowerCase());
          }) ?? []
        );
      }

      // Otherwise, filter the weeklyCheckInTableDataShowing by user name or email
      return (
        weeklyCheckInTableDataShowing?.filter((row) => {
          const user = users[getWeeklyCheckInTableRowEntityId(row)];
          if (!user) {
            return false;
          }
          const { firstName = "", lastName = "", emailAddress } = user;
          const fullName = `${firstName} ${lastName}`.trim();
          return (
            fullName.toLowerCase().includes(displayedInput.toLowerCase()) ||
            emailAddress.toLowerCase().includes(displayedInput.toLowerCase())
          );
        }) ?? []
      );
    }, [
      users,
      displayedInput,
      entitySelected,
      departments,
      allTeamsById,
      weeklyCheckInTableDataShowing,
    ]);

    const getOverallScore = (data: WeeklyCheckInTableRowData) => {
      const { moodScore, progressScore, readinessScore } = data;
      const totalScore =
        (moodScore ?? 0) + (progressScore ?? 0) + (readinessScore ?? 0);
      const dotScore = totalScore / 3;
      const dotColor = getOutOfFiveColor(dotScore);

      const popoverElement = isWeeklyCheckInRowUser(data)
        ? getNoScorePopoverElement(
            "overall",
            data.threadId,
            data.errorGeneratingScores,
            data.gradingCount
          )
        : null;

      const displayValue = dotScore ? (
        <div className="row-gap-8px align-items-center ms-2">
          {formatNumber(dotScore)}
          <div className={`status-dot ${dotColor}`} />
        </div>
      ) : (
        <div className="d-flex ms-2">
          <p>-</p>
          {popoverElement ?? (
            <FontAwesomeIcon
              icon={["far", "exclamation-triangle"]}
              style={{ paddingLeft: "5px" }}
            />
          )}
        </div>
      );

      return {
        sortValue: dotScore,
        displayValue: displayValue,
      };
    };

    const getStatusCell = (userData: UserWeeklyCheckInTableRowData) => {
      if (userData.wasConversationGraded) {
        return {
          displayValue: (
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faCircle}
                className="me-2 text-[10px] text-success-green-100"
              />
              Complete
            </div>
          ),
          sortValue: 0,
        };
      }
      const regradingWeeklyCheckinStatus =
        regradingWeeklyCheckinStatusByThread?.[userData.threadId];

      if (
        regradingWeeklyCheckinStatus === "loading" ||
        (userData.completedDate &&
          (!userData.errorGeneratingScores || !userData.gradingCount))
      ) {
        return {
          displayValue: (
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faCircle}
                className="me-2 text-[10px] text-yellow-100"
              />
              Pending
            </div>
          ),
          sortValue: 1,
        };
      }

      return {
        displayValue: (
          <div className="d-flex align-items-center text-muted">
            <FontAwesomeIcon
              icon={["far", "exclamation-triangle"]}
              className="me-2"
            />
            Skipped
          </div>
        ),
        sortValue: 2,
      };
    };

    const getSummaryCell = (
      threadId: string,
      primaryTheme?: string | null,
      wasConversationGraded?: boolean
    ) => {
      const regradingWeeklyCheckinStatus =
        regradingWeeklyCheckinStatusByThread?.[threadId];
      return {
        displayValue: (
          <div className="row-gap-8px align-items-center justify-content-between">
            <p>{primaryTheme}</p>
            {!wasConversationGraded && (
              <Button
                variant="secondary-gray"
                onClick={() => {
                  dispatch(regradeWeeklyCheckin(threadId));
                }}
              >
                <FontAwesomeIcon
                  icon={faArrowRotateForward}
                  spin={regradingWeeklyCheckinStatus === "loading"}
                  className="me-2"
                />
                {regradingWeeklyCheckinStatus === "loading"
                  ? "Rescoring..."
                  : "Rescore"}
              </Button>
            )}
          </div>
        ),
        sortValue: primaryTheme ?? "",
      };
    };

    const getUserPatternCell = (data: UserWeeklyCheckInTableRowData) => {
      const popoverTitle = getRiskPattern(
        !!data.isBurnoutRisk,
        !!data.isTurnoverRisk
      );
      const popoverContent = WEEKLY_CHECK_IN_PATTERN_CONTEXT[popoverTitle];

      const severityMap = {
        "May Need Support": { score: 10, sort: 3 },
        "Recent Challenge": { score: 6, sort: 2 },
        "Things Improving": { score: 6, sort: 1 },
        "No Issues": { score: 10, sort: 0 },
      };

      const { score: severityScore, sort: sortValue } =
        severityMap[popoverTitle];

      return {
        displayValue: (
          <SimplePopover
            popoverContent={<p>{popoverContent}</p>}
            trigger="hover"
            popoverTitle={popoverTitle}
            hideCloseButton
          >
            <SupportOrCelebrateDot
              score={severityScore}
              type={popoverTitle === "No Issues" ? "celebrate" : "support"}
            />
          </SimplePopover>
        ),
        sortValue: sortValue,
      };
    };

    const getUserPatternHeaderTooltip = () => {
      const arr: {
        score: number;
        type: "celebrate" | "support";
        text: keyof typeof WEEKLY_CHECK_IN_PATTERN_CONTEXT;
      }[] = [
        {
          score: 10,
          type: "celebrate",
          text: "No Issues",
        },
        {
          score: 6,
          type: "support",
          text: "Things Improving",
        },
        {
          score: 6,
          type: "support",
          text: "Recent Challenge",
        },
        {
          score: 10,
          type: "support",
          text: "May Need Support",
        },
      ];

      return (
        <SimplePopover
          popoverContent={
            <div className="d-flex gap-2 flex-col">
              {arr.map((item) => (
                <div key={item.text}>
                  <div className="d-inline-block">
                    <SupportOrCelebrateDot
                      score={item.score}
                      type={item.type}
                    />
                  </div>{" "}
                  <b>{item.text}</b> -{" "}
                  {WEEKLY_CHECK_IN_PATTERN_CONTEXT[item.text]}
                </div>
              ))}
            </div>
          }
          trigger="hover"
          popoverTitle="Pattern"
        >
          <FontAwesomeIcon
            icon={["far", "circle-info"]}
            className="ms-1 grey-text"
          />
        </SimplePopover>
      );
    };

    const getConcernCell = (data: UserWeeklyCheckInTableRowData) => {
      const concern = data.hrConcern;
      if (!concern) {
        return { displayValue: null, sortValue: 0 };
      }

      const HR_CONCERN_SEVERITY_CONSTANT =
        HR_CONCERN_SEVERITY_MAP[concern.severity];
      const severityScore = HR_CONCERN_SEVERITY_CONSTANT?.sortValue ?? 0;
      const severityLabel =
        HR_CONCERN_SEVERITY_CONSTANT?.label ??
        concern.severity.charAt(0).toUpperCase() + concern.severity.slice(1);

      const categoryLabels = concern.categories
        .map((c) =>
          c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
        )
        .join(", ");

      return {
        sortValue: severityScore,
        displayValue: (
          <SimplePopover
            trigger="hover"
            popoverTitle={`${severityLabel} Concern Detected`}
            popoverContent={
              <div>
                <p>
                  <strong>Categories:</strong> {categoryLabels}
                </p>
                {concern.excerpt && !data.isSummaryHidden && (
                  <p className="mt-1">
                    <strong>Excerpt:</strong> &ldquo;{concern.excerpt}&rdquo;
                  </p>
                )}
              </div>
            }
          >
            <span
              className={
                HR_CONCERN_SEVERITY_CONSTANT.className ?? "concern-badge"
              }
            >
              <FontAwesomeIcon
                icon={["far", "triangle-exclamation"]}
                className="me-1"
              />
              {severityLabel}
            </span>
          </SimplePopover>
        ),
      };
    };

    const getActiveAlerts = (): ConcernAlertRow[] => {
      if (!weeklyCheckInCompanySettings?.isHarmfulDetectionEnabledForCheckIns) {
        return [];
      }
      const result: ConcernAlertRow[] = [];
      getFilteredRows().forEach((row) => {
        if (isWeeklyCheckInRowUser(row) && row.hrConcern) {
          result.push({
            alertId: row.hrConcern.id,
            userAccountId: row.userAccountId,
            threadId: row.threadId,
            isSummaryHidden: row.isSummaryHidden,
            date: row.completedDate,
            severity: row.hrConcern.severity,
            excerpt: row.hrConcern.excerpt,
            detectedAt: row.hrConcern.detectedAt,
          });
        }
      });
      return result;
    };

    const getActiveConcernsBanner = () => {
      if (!weeklyCheckInCompanySettings?.isHarmfulDetectionEnabledForCheckIns) {
        return null;
      }
      const activeAlerts = getActiveAlerts();
      if (activeAlerts.length === 0) return null;
      const count = activeAlerts.length;

      return (
        <div className="warning-banner light-red justify-content-between row-gap-12px align-items-center">
          <div className="row-gap-8px align-items-center">
            <FontAwesomeIcon
              icon={["far", "triangle-exclamation"]}
              className="me-2"
            />
            <p>
              {count} concerning check-in response{count > 1 ? "s" : ""}{" "}
              detected that require your attention.
            </p>
          </div>
          <Button
            variant="secondary-blue"
            onClick={() => setIsConcernAlertsModalOpen(true)}
          >
            View {count > 1 ? `${count} Concerns` : "Concern"}
          </Button>
        </div>
      );
    };

    const getTableRow = (data: WeeklyCheckInTableRowData) => {
      if (isWeeklyCheckInRowUser(data)) {
        const overallScore = getOverallScore(data);
        return {
          moodScore: getScoreCell(data, "moodScore"),
          progressScore: getScoreCell(data, "progressScore"),
          readinessScore: getScoreCell(data, "readinessScore"),
          user: getUserCell(
            data.userAccountId,
            data.threadId,
            data.completedDate
          ),
          date: getDateCell(data.completedDate),
          dropdown: getUserDropdownCell(data),
          overallScore,
          pattern: getUserPatternCell(data),
          status: getStatusCell(data),
          summary: getSummaryCell(
            data.threadId,
            data.primaryTheme,
            data.wasConversationGraded
          ),
          concern:
            weeklyCheckInCompanySettings?.isHarmfulDetectionEnabledForCheckIns
              ? getConcernCell(data)
              : { displayValue: null, sortValue: 0 },
        };
      }

      const overallScore = getOverallScore(data);

      const noUsersCompletedValue =
        data.completedCount === 0
          ? {
              sortValue: 0,
              displayValue: "-",
            }
          : null;

      const groupContent = {
        overallScore: noUsersCompletedValue ?? overallScore,
        moodScore: noUsersCompletedValue ?? getScoreCell(data, "moodScore"),
        progressScore:
          noUsersCompletedValue ?? getScoreCell(data, "progressScore"),
        readinessScore:
          noUsersCompletedValue ?? getScoreCell(data, "readinessScore"),
        date: getDateCell(data.startDate, data.endDate),
        completionRate: percentageCell(data.invitedCount, data.completedCount),
        dropdown: getGroupDropdownCell(data),
        burnoutRiskCount:
          noUsersCompletedValue ??
          percentageCell(
            data.completedCount,
            data.burnoutRiskCount,
            true,
            true
          ),
        turnoverRiskCount:
          noUsersCompletedValue ??
          percentageCell(
            data.completedCount,
            data.turnoverRiskCount,
            true,
            true
          ),
      };

      if (entitySelected === "department") {
        return {
          ...groupContent,
          department: getDepartmentCell(data),
        };
      }

      return {
        ...groupContent,
        team: getTeamCell(data),
      };
    };

    const haveAllPendingUsersBeenReminded = useMemo(() => {
      const result: { [userAccountId: number]: boolean } = {};
      const { usersWhoHaveBeenRemindedInTheLastFiveMinutes = [] } =
        weeklyCheckInUserStatus ?? {};
      usersWhoHaveBeenRemindedInTheLastFiveMinutes.forEach((userAccountId) => {
        result[userAccountId] = true;
      });

      const allUserHaveBeenReminded = !!incompleteUserAccountIds?.every(
        (userAccountId) => result[userAccountId]
      );
      return allUserHaveBeenReminded;
    }, [weeklyCheckInUserStatus, incompleteUserAccountIds]);

    const getTableRows = () => getFilteredRows().map(getTableRow);

    const getRemindMembersBanner = () => {
      if (!incompleteUserAccountIds?.length || isSample) {
        return null;
      }
      const count = incompleteUserAccountIds?.length ?? 0;
      return (
        <div className="warning-banner blue justify-content-between row-gap-12px align-items-center">
          <div className="row-gap-8px align-items-center">
            <FontAwesomeIcon icon={["far", "lightbulb-on"]} className="me-2" />

            <p>
              Send email reminders to {count} {getPeopleOrPerson(count)} who
              haven't completed the check-in for this period?
            </p>
          </div>
          <div className="row-gap-8px">
            <Button
              variant="secondary-blue"
              onClick={() => {
                setIncompleteCheckInsModalState(true);
              }}
              className="nowrap"
            >
              See who hasn't completed
            </Button>
            <ReminderButton
              disabled={sendEmailReminderStatus === "loading"}
              className="nowrap"
              reminderSent={haveAllPendingUsersBeenReminded}
              variant="primary"
              onClick={() => {
                setIsSendAllRemindersModalOpened(true);
              }}
              sendButtonText="Send Reminders"
              sentButtonText="Reminders Sent"
            />
          </div>
        </div>
      );
    };

    const getSearchPlaceholder = () => {
      if (breakdownRowSelected) {
        return "Search for a user...";
      }
      switch (entitySelected) {
        case "team":
          return "Search for a team...";
        case "department":
          return "Search for a department...";
        case "user":
        default:
          return "Search for a user...";
      }
    };

    const getNameCSVCellValue = useCallback(
      (entityId: number, entitySelected: string) => {
        if (entitySelected === "department") {
          return departments[entityId]?.name ?? "";
        }

        if (entitySelected === "team") {
          return allTeamsById[entityId]?.teamName ?? "";
        }
        const user = users[entityId];
        const { firstName = "", lastName = "" } = user ?? {};
        return `${firstName} ${lastName}`.trim();
      },
      [users, departments, allTeamsById]
    );

    const getTeamCSVData = useCallback(
      (data: GroupWeeklyCheckInTableRowData) => {
        const invitedLength = data.invitedCount;
        return {
          completionRate: invitedLength
            ? ((data.completedCount / invitedLength) * 100).toFixed(2)
            : 0,
          burnOutRiskScore: data.burnoutRiskCount ?? "-",
          turnOverRiskScore: data.turnoverRiskCount ?? "-",
        };
      },
      []
    );

    const getManagerCSVValue = useCallback(
      (userAccountId: number) => {
        const managerInformationObject =
          managersAndDirectReportsByUserAccountId[userAccountId];
        if (!managerInformationObject?.managerRelationship) {
          return "-";
        }
        const managerId =
          managerInformationObject.managerRelationship.managerUserAccountId;
        const managerName = `${usersById[managerId]?.firstName ?? ""} ${
          usersById[managerId]?.lastName ?? ""
        }`;
        return managerName;
      },
      [managersAndDirectReportsByUserAccountId, usersById]
    );

    const getTeamCSVValue = useCallback(
      (teamIds: number[] = []) => {
        const teamNames: string[] = [];
        teamIds?.forEach((teamId) => {
          const team = allTeamsById[teamId];
          if (!team) return;
          const { teamName } = team;
          teamNames.push(teamName);
        });
        if (teamNames.length === 0) return "-";
        return teamNames.join(", ");
      },
      [allTeamsById]
    );

    const getPatternCSVValue = (
      data: UserWeeklyCheckInTableRowData
    ): keyof typeof WEEKLY_CHECK_IN_PATTERN_CONTEXT => {
      return getRiskPattern(!!data.isBurnoutRisk, !!data.isTurnoverRisk);
    };

    const getUserCSVData = useCallback(
      (data: UserWeeklyCheckInTableRowData) => {
        const userInfo = usersById[data.userAccountId];
        const { firstName = "", lastName = "" } = userInfo ?? {};
        return {
          burnOutRiskScore: data.isBurnoutRisk ? "Yes" : "No",
          turnOverRiskScore: data.isTurnoverRisk ? "Yes" : "No",
          celebrateScore: `${data.winSpotlight?.magnitude ?? "-"}`,
          supportScore: `${data.issueSpotlight?.severity ?? "-"}`,
          winSpotlightAchievement: `${data.winSpotlight?.achievement ?? "-"}`,
          winSpotlightImpact: `${data.winSpotlight?.impact ?? "-"}`,
          winSpotlightRecognition: `${data.winSpotlight?.recognition ?? "-"}`,
          issueSpotlightIssue: `${data.issueSpotlight?.issue ?? "-"}`,
          issueSpotlightImpact: `${data.issueSpotlight?.impact ?? "-"}`,
          issueSpotlightAction: `${data.issueSpotlight?.action ?? "-"}`,
          pattern: getPatternCSVValue(data),
          conversationStarters: data.conversationStarters?.length
            ? `[${data.conversationStarters?.join(", ")}]`
            : "-",
          firstName,
          lastName,
          manager: getManagerCSVValue(data.userAccountId),
          teams: getTeamCSVValue(userInfo?.teamIds),
          status: data.wasConversationGraded ? "Completed" : "Error",
          summary: data.primaryTheme ?? "-",
        };
      },
      [usersById, getManagerCSVValue, getTeamCSVValue]
    );

    const getCSVExportRows = useCallback(() => {
      trackCSVExport("Check-In Activity");
      return getFilteredRows().map((data) => {
        const entityId =
          "userAccountId" in data ? data.userAccountId : data.entityId;
        const totalScore =
          (data.moodScore ?? 0) +
          (data.progressScore ?? 0) +
          (data.readinessScore ?? 0);
        const overallScore = totalScore / 3;
        const baseRow = {
          [entitySelected]: getNameCSVCellValue(entityId, entitySelected),
          date: getDateForSorting(data).toDateString(),
          moodScore: data.moodScore ?? "N/A",
          progressScore: data.progressScore ?? "N/A",
          readinessScore: data.readinessScore ?? "N/A",
          overallScore: totalScore ? overallScore.toFixed(2) : "N/A",
        };

        if (!("threadId" in data)) {
          return {
            ...baseRow,
            ...getTeamCSVData(data),
          };
        }

        return {
          ...baseRow,
          ...getUserCSVData(data),
        };
      });
    }, [
      getFilteredRows,
      getNameCSVCellValue,
      entitySelected,
      getUserCSVData,
      getTeamCSVData,
    ]);

    const getUserHeaders = () => {
      const concernHeader =
        weeklyCheckInCompanySettings?.isHarmfulDetectionEnabledForCheckIns
          ? [{ label: "Concern", key: "concern" }]
          : [];

      if (weeklyCheckInCompanySettings?.visibilityMode !== "open") {
        return [
          { label: "User", key: entitySelected },
          { label: "Date", key: "date" },
          { label: "Status", key: "status" },
          { label: "Summary", key: "summary" },
          ...concernHeader,
          { label: "", key: "dropdown", disableSort: true },
        ];
      }

      return [
        { label: "User", key: entitySelected },
        { label: "Date", key: "date" },
        {
          label: "Overall score",
          key: "overallScore",
          additionalContent: (
            <CheckinScorePopover type="overall" showColors={false} />
          ),
        },
        {
          label: "Mood score",
          key: "moodScore",
          additionalContent: <CheckinScorePopover type="mood" />,
        },
        {
          label: "Progress score",
          key: "progressScore",
          additionalContent: <CheckinScorePopover type="progress" />,
        },
        {
          label: "Readiness score",
          key: "readinessScore",
          additionalContent: <CheckinScorePopover type="readiness" />,
        },
        {
          label: "Pattern",
          key: "pattern",
          additionalContent: getUserPatternHeaderTooltip(),
        },
        ...concernHeader,
        { label: "", key: "dropdown", disableSort: true },
      ];
    };

    const getGroupHeaders = () => {
      const nameHeaderLabel =
        entitySelected.charAt(0).toUpperCase() + entitySelected.slice(1);
      return [
        { label: nameHeaderLabel, key: entitySelected },
        { label: "Date", key: "date" },
        {
          label: "Overall score",
          key: "overallScore",
          additionalContent: (
            <CheckinScorePopover type="overall" showColors={false} />
          ),
        },
        {
          label: "Mood score",
          key: "moodScore",
          additionalContent: <CheckinScorePopover type="mood" />,
        },
        {
          label: "Progress score",
          key: "progressScore",
          additionalContent: <CheckinScorePopover type="progress" />,
        },
        {
          label: "Readiness score",
          key: "readinessScore",
          additionalContent: <CheckinScorePopover type="readiness" />,
        },
        {
          label: "People At Risk of Burnout",
          key: "burnoutRiskCount",
          additionalContent: (
            <CheckinScorePopover type="burnoutRisk" showColors={false} />
          ),
        },
        {
          label: "People At Risk of Turnover",
          key: "turnoverRiskCount",
          additionalContent: (
            <CheckinScorePopover type="turnoverRisk" showColors={false} />
          ),
        },
      ];
    };

    const getHeaders = () => {
      if (entitySelected === "user") {
        return getUserHeaders();
      }
      return getGroupHeaders();
    };

    const getCSVExportHeaders = (): TableHeader[] => {
      let scoreHeaders = [
        {
          label: "Overall score",
          key: "overallScore",
        },
        {
          label: "Mood score",
          key: "moodScore",
        },
        {
          label: "Progress score",
          key: "progressScore",
        },
        {
          label: "Readiness score",
          key: "readinessScore",
        },
      ];

      if (entitySelected === "user") {
        scoreHeaders.push(
          {
            label: "Pattern",
            key: "pattern",
          },
          {
            label: "Burnout risk",
            key: "burnOutRiskScore",
          },
          {
            label: "Turnover risk",
            key: "turnOverRiskScore",
          },
          {
            label: "Celebrate score",
            key: "celebrateScore",
          },
          {
            label: "Support score",
            key: "supportScore",
          }
        );

        if (weeklyCheckInCompanySettings?.visibilityMode !== "open") {
          scoreHeaders = [
            {
              label: "Status",
              key: "status",
            },
            {
              label: "Summary",
              key: "summary",
            },
          ];
        }

        return [
          {
            label: "First name",
            key: "firstName",
          },
          {
            label: "Last name",
            key: "lastName",
          },
          {
            label: "Manager",
            key: "manager",
          },
          {
            label: "Teams",
            key: "teams",
          },
          { label: "Date", key: "date" },
          ...scoreHeaders,
          {
            label: "Win Spotlight - Achievement",
            key: "winSpotlightAchievement",
          },
          {
            label: "Win Spotlight - Impact",
            key: "winSpotlightImpact",
          },
          {
            label: "Win Spotlight - Recognition",
            key: "winSpotlightRecognition",
          },
          {
            label: "Issue Spotlight - Issue",
            key: "issueSpotlightIssue",
          },
          {
            label: "Issue Spotlight - Impact",
            key: "issueSpotlightImpact",
          },
          {
            label: "Issue Spotlight - Action",
            key: "issueSpotlightAction",
          },
          {
            label: "Conversation Starters",
            key: "conversationStarters",
          },
        ];
      }

      return [
        {
          label:
            entitySelected.charAt(0).toUpperCase() + entitySelected.slice(1),
          key: entitySelected,
        },
        { label: "Date", key: "date" },
        {
          label: "Completion Rate",
          key: "completionRate",
        },
        ...scoreHeaders,
        {
          label: "People At Risk of Burnout",
          key: "burnoutRiskCount",
        },
        {
          label: "People At Risk of Turnover",
          key: "turnoverRiskCount",
        },
      ];
    };

    const getEmptyState = () => {
      if (fetchTableDataLoading) {
        return null;
      }

      if (rows.length !== 0) {
        return null;
      }

      if (displayedInput) {
        return (
          <EmptyCard
            title={`No ${entitySelected} found matching "${displayedInput}"`}
            bodyText="Try searching for a different name."
          />
        );
      }

      return (
        <EmptyCard
          title="No check-in activity found"
          bodyText="Once there is check-in activity, it will be shown here."
        />
      );
    };

    const getDropdownSection = () => {
      if (breakdownRowSelected || isSample) {
        return null;
      }

      const isConservativeMode =
        weeklyCheckInCompanySettings?.visibilityMode !== "open";

      const CheckInEntityTypes = {
        ...WeeklyCheckInEntityTypes,
      };

      if (hideDepartmentDropdown || isDepartmentsHidden) {
        delete CheckInEntityTypes["department"];
      }

      return (
        <div className="d-flex justify-content-between align-items-center">
          <div className="row-gap-12px">
            {getDropdownFromRecord(
              WeeklyCheckInTimeIntervals,
              timeInterval,
              (e) => {
                setTimeInterval(e ?? WeeklyCheckInInitialTimeInterval);
                setDisplayedInput("");
              },
              fetchTableDataLoading
            )}
            {!hideEntityDropdown &&
              !isConservativeMode &&
              getDropdownFromRecord(
                CheckInEntityTypes,
                entitySelected,
                (e) => {
                  setEntitySelected(e ?? "user");
                  setDisplayedInput("");
                },
                fetchTableDataLoading
              )}
          </div>
          <div>
            <ExportCSVButton
              headers={getCSVExportHeaders()}
              getRows={getCSVExportRows}
              fileName="Check-In Activity"
            />
          </div>
        </div>
      );
    };

    const rows = getTableRows();
    const headers = getHeaders();

    useImperativeHandle(ref, () => ({
      exportCSV: () => {
        trackCSVExport("Check-In Activity");
        return getCSVExportRows();
      },
    }));

    const incompleteUserAccountIdsArray = incompleteUserAccountIds ?? [];

    return (
      <>
        <CheckInResponseSummaryModal
          show={checkInResponseModalState.isOpen}
          onHide={() => {
            setCheckInResponseModalState({
              isOpen: false,
              threadId: "",
              userAccountId: null,
              isSample: false,
            });
          }}
          threadId={checkInResponseModalState.threadId}
          userAccountId={checkInResponseModalState.userAccountId}
          isSample={checkInResponseModalState.isSample}
          date={checkInResponseModalState.date}
        />
        <IncompleteCheckInsModal
          show={incompleteCheckInsModalState}
          onShow={setIncompleteCheckInsModalState}
          userAccountIds={incompleteUserAccountIdsArray}
        />
        <SendAllPendingCheckinReminderWarningModal
          isOpen={isSendAllRemindersModalOpened}
          onHide={() => setIsSendAllRemindersModalOpened(false)}
          onConfirmClick={async () => {
            await dispatch(
              sendWeeklyCheckInReminders(incompleteUserAccountIdsArray)
            );
            setIsSendAllRemindersModalOpened(false);
          }}
          peopleCount={incompleteUserAccountIdsArray?.length}
          isInProgress={sendEmailReminderStatus === "loading"}
        />
        <HrConcernAlertsModal
          show={isConcernAlertsModalOpen && !checkInResponseModalState.isOpen}
          onHide={() => setIsConcernAlertsModalOpen(false)}
          activeAlerts={getActiveAlerts()}
          onViewSummary={(alert) =>
            openCheckInResponseModal(
              alert.threadId,
              alert.userAccountId,
              alert.date
            )
          }
        />
        {getActiveConcernsBanner()}
        {getRemindMembersBanner()}
        {getSearchInput(
          getSearchPlaceholder(),
          displayedInput,
          setDisplayedInput
        )}
        {getDropdownSection()}
        <SortableTable
          rows={rows}
          tableClassName="admin-console-table"
          columnHeaders={[...headers]}
          isFirstColumnSticky
          isLoading={fetchTableDataLoading}
        />
        {getEmptyState()}
      </>
    );
  }
);

export default CheckInActivityTableContent;
